import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router";
import { Home, User, Menu, Bell, Calendar } from "lucide-react";
import { useMe } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { auth, db } from "@/lib/firebase";
import { signInWithCustomToken } from "firebase/auth";
import { collection, query, onSnapshot } from "firebase/firestore";
import { useLanguage } from "@/lib/useLanguage";
import { useTranslation } from "@/lib/i18n";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetClose,
} from "@/components/ui/sheet";

/**
 * Canonical Kidology top navigation per `wireframes/catalog_page/desktop_header.png`
 * and `wireframes/catalog_page/full_mobile_view.png`.
 *
 * - Coral wordmark "Kidology" → links to /courses (the platform's home surface).
 * - 4-tab nav: Courses, Library, 1-on-1 Consultations + auth-adaptive 4th slot:
 *     - Authenticated: Profile tab.
 *     - Anonymous: outline "Log in" button.
 * - Active tab styled with bg-primary/10 + text-primary + ring-primary/15 and aria-current="page".
 * - Quick task 260512-egd (fix #2): below `lg:`, the horizontal-scroll tab strip
 *   is replaced by a hamburger button on the right of the wordmark that opens a
 *   left-side `<Sheet>` containing the same 4 nav items in a vertical stack.
 *   Tapping a nav link, the sheet overlay, or pressing Esc closes the drawer.
 * - Desktop (≥lg): nav inline with the wordmark on the right (unchanged).
 */

import { getTenantConfig } from "@/lib/tenant";
import { isArticlePath } from "@/components/article/articleStyles";

export function TopNav() {
  const tenant = getTenantConfig();
  const { data: me } = useMe();
  const { pathname } = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navHidden = isArticlePath(pathname);
  const isActive = (href: string) =>
    pathname === href || pathname.startsWith(`${href}/`);

  const showConsultations = tenant.hasConsultations && Boolean(me?.hasCoachSubscription);



  const [hasUnread, setHasUnread] = useState(false);


  useEffect(() => {
    if (!me?.firebaseCustomToken || !showConsultations) {
      setHasUnread(false);
      return;
    }

    let unsubscribe: () => void = () => {};

    const getUidFromToken = (token: string): string => {
      try {
        const payloadBase64 = token.split(".")[1];
        const payloadJson = atob(payloadBase64);
        const payload = JSON.parse(payloadJson);
        return payload.uid || payload.sub;
      } catch (e) {
        return "";
      }
    };

    const initListener = async () => {
      try {
        const uid = getUidFromToken(me.firebaseCustomToken!);
        if (!uid) return;

        if (!auth.currentUser || auth.currentUser.uid !== uid) {
          await signInWithCustomToken(auth, me.firebaseCustomToken!);
        }

        const q = query(collection(db, "chats", uid, "messages"));
        unsubscribe = onSnapshot(q, (snapshot) => {
          const unreadExists = snapshot.docs.some((d) => {
            const data = d.data();
            return data.sender === "Admin" && !data.isRead;
          });
          setHasUnread(unreadExists);
        });
      } catch (err) {
        console.warn("[TopNav Firebase Listener] Failed:", err);
      }
    };

    initListener();

    return () => {
      unsubscribe();
    };
  }, [me?.firebaseCustomToken, showConsultations]);

  const { t } = useTranslation();

  const tabs = [
    ...(tenant.hasReport ? [{ href: "/report", label: t("nav_report"), icon: Bell }] : []),
    ...(tenant.hasCourses ? [{ href: "/courses", label: t("nav_courses"), icon: Home }] : []),
    ...(showConsultations ? [{ href: "/consultations", label: t("nav_consultations"), icon: Calendar, hasBadge: hasUnread }] : []),
  ];






  // The auth-adaptive 4th slot — Profile tab when logged in, Log in button when anonymous.
  const authSlot = me ? (
    tenant.id === "mindfulme" ? (
      // MindfulMe: outlined "Profile" pill (profile → manage / cancel subscription)
      <Link
        to="/profile"
        aria-current={isActive("/profile") ? "page" : undefined}
        className={cn(
          "ml-2 inline-flex h-10 shrink-0 items-center gap-2 rounded-full border px-4 text-sm font-medium text-slate-900 transition-colors hover:bg-slate-50",
          isActive("/profile") ? "border-slate-400" : "border-slate-200",
        )}
      >
        <User className="size-4" aria-hidden="true" />
        {t("nav_profile")}
      </Link>
    ) : (
      <NavTab href="/profile" label={t("nav_profile")} icon={User} active={isActive("/profile")} />
    )
  ) : (
    <Button asChild variant="outline" size="sm" className="shrink-0 lg:ml-2">
      <Link to="/login">{t("nav_login")}</Link>
    </Button>
  );

  // Drawer variant of the same items — vertical stack, full-width tap targets.
  const drawerAuthSlot = me ? (
    <SheetClose asChild>
      <NavTab
        href="/profile"
        label={t("nav_profile")}
        icon={User}
        active={isActive("/profile")}
        fullWidth
      />
    </SheetClose>
  ) : (
    <SheetClose asChild>
      <Button asChild variant="outline" size="sm" className="w-full justify-start">
        <Link to="/login">{t("nav_login")}</Link>
      </Button>
    </SheetClose>
  );

  const { language, setLanguage } = useLanguage();

  const renderLanguageSwitcher = (triggerClassName?: string) => (
    <Select value={language} onValueChange={(val) => setLanguage(val as any)}>
      <SelectTrigger className={cn("w-[135px] h-9", triggerClassName)}>
        <SelectValue placeholder="Language" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="en">English</SelectItem>
        <SelectItem value="es">Español</SelectItem>
        <SelectItem value="pt">Português</SelectItem>
      </SelectContent>
    </Select>
  );

  return (
    // Quick task 260512-q7q — sticky frosted header gives the app a
    // contemporary feel. supports-[backdrop-filter] guard means non-blur
    // browsers fall back to a more-opaque background; no regression.
    // Fixed 64px height (h-16) on the inner row so the sticky nav doesn't
    // jank as it crosses page content.
    <header className="sticky top-0 z-50 border-b border-border/60 bg-background shadow-xs">


      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-3 px-4 lg:px-6">
        <Link
          to={tenant.homePath === "/courses" ? "/courses" : "/consultations"}
          className="flex items-center gap-2.5 text-xl font-bold tracking-tight text-foreground sm:text-2xl"
        >
          {tenant.brandName === "MindfulMe" ? (
            <span className="flex items-center gap-2 font-black text-slate-900 tracking-tight text-xl sm:text-2xl">
              <span className="relative flex size-7 items-center justify-center">
                <span className="absolute left-0 size-5 rounded-full bg-indigo-600" />
                <span className="absolute right-0 size-5 rounded-full bg-orange-500 opacity-90 mix-blend-multiply" />
              </span>
              <span>Mindful<span className="text-indigo-600">Me</span></span>
            </span>
          ) : (
            <span className="text-primary font-bold">{tenant.brandName}</span>
          )}
        </Link>


        {!navHidden && (
          <>
            {/* Desktop tabs (≥lg) inline with wordmark */}
            <nav className="hidden items-center gap-1 lg:flex" aria-label="Primary">
              {tabs.map((t) => (
                <NavTab key={t.href} {...t} active={isActive(t.href)} />
              ))}
              {authSlot}
              <div className="ml-2">
                {renderLanguageSwitcher()}
              </div>
            </nav>
            {/* Mobile (<lg): MindfulMe keeps the Profile pill visible next to the hamburger */}
            {me && tenant.id === "mindfulme" && <div className="ml-auto lg:hidden">{authSlot}</div>}
            {/* Mobile (<lg): hamburger button opens a left-side drawer */}
            <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="lg:hidden"
                aria-label="Open menu"
                onClick={() => setDrawerOpen(true)}
              >
                <Menu className="size-5" aria-hidden="true" />
              </Button>
              <SheetContent side="left" className="w-72 sm:max-w-sm">
                <SheetHeader>
                  <SheetTitle className="text-xl font-bold tracking-tight text-primary">
                    {tenant.brandName}
                  </SheetTitle>

                </SheetHeader>
                <nav className="flex flex-col gap-1 px-4 pb-4" aria-label="Primary mobile">
                  {tabs.map((t) => (
                    <SheetClose key={t.href} asChild>
                      <NavTab {...t} active={isActive(t.href)} fullWidth />
                    </SheetClose>
                  ))}
                  {drawerAuthSlot}
                  <div className="mt-4 pt-4 border-t border-border">
                    <p className="text-xs text-muted-foreground mb-2 font-medium">{t("nav_language")}</p>
                    {renderLanguageSwitcher("w-full")}
                  </div>
                </nav>

              </SheetContent>
            </Sheet>
          </>
        )}
      </div>
    </header>
  );
}

function NavTab({
  href,
  label,
  icon: Icon,
  active,
  fullWidth = false,
  hasBadge = false,
}: {
  href: string;
  label: string;
  icon: typeof Home;
  active: boolean;
  fullWidth?: boolean;
  hasBadge?: boolean;
}) {
  // Quick task 260512-q7q — add duration-150 so the hover-color fade is
  // intentional, and a hairline inset ring on the active state so the
  // bg-primary/10 pill reads as a deliberate pill rather than a tinted div.
  // (Phase 7 D-07: pill was bg-action-soft before warm-coral token flip.)
  const base = fullWidth
    ? "flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors duration-150 ring-1 ring-inset ring-transparent"
    : "flex shrink-0 items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors duration-150 ring-1 ring-inset ring-transparent";
  return (
    <Link
      to={href}
      aria-current={active ? "page" : undefined}
      className={`${base} ${
        active
          ? "bg-primary/10 text-primary font-medium ring-primary/15"
          : "text-foreground hover:bg-muted/70"
      }`}
    >
      <Icon className="size-4" aria-hidden="true" />
      <span className="relative mr-4">
        {label}
        {hasBadge && (
          <span className="absolute -top-1 -right-4 flex items-center justify-center">
            <Bell className="size-3 text-yellow-500 fill-yellow-500" />
            <span className="absolute -top-0.5 -right-0.5 size-1.5 rounded-full bg-rose-600 ring-1 ring-background" />
          </span>
        )}
      </span>
    </Link>
  );
}
